// wizardHooks.ts - Custom hooks for ProductCreationWizard
import { useState, useCallback, useEffect, useRef } from "react"
import { useLocation, type Location } from "react-router-dom"
import { useComponentUnsavedChanges } from "src/contexts/UnsavedChangesContext"
import type { ProductData } from "../components/ProductCreationWizard/ProductCreationWizard"
import type {
  BasicInfoData,
  ClassificationData,
  SalesInfoData,
  ShippingData,
  ReviewData
} from "../components/WizardSteps"
import { createDefaultFormData, EMPTY_FORM_DATA } from "./wizardConstants"
import { validateStep, validateAllSteps } from "./wizardValidation"
import {
  saveFormData,
  loadFormData,
  clearPersistedData,
  setReloadFlag,
  checkAndClearReloadFlag,
  clearReloadFlag,
  handlePageShow,
  cleanupOnUnmount,
  checkForMeaningfulData
} from "./wizardPersistence"

// Hook for managing wizard form state
export function useWizardState(
  initialData?: Partial<ProductData>,
  mode: 'create' | 'edit' = 'create'
) {
  const [formData, setFormData] = useState<ProductData>(() => 
    createDefaultFormData(initialData, mode)
  )
  
  const [resetKey, setResetKey] = useState(0)

  const resetForm = useCallback(() => {
    setFormData(EMPTY_FORM_DATA)
    setResetKey((prev) => prev + 1)
  }, [])

  const updateFormData = useCallback((
    stepKey: keyof ProductData,
    data: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      [stepKey]: data
    }))
  }, [])

  return {
    formData,
    setFormData,
    resetKey,
    resetForm,
    updateFormData
  }
}

// Hook for managing wizard step navigation
export function useWizardNavigation(totalSteps: number = 5) {
  const [currentStep, setCurrentStep] = useState(1)
  const [stepErrors, setStepErrors] = useState<Record<number, string[]>>({})

  const handleStepClick = useCallback((stepNumber: number, formData: ProductData) => {
    if (stepNumber !== currentStep) {
      if (stepNumber < currentStep) {
        // Allow going back without validation
        setCurrentStep(stepNumber)
      } else if (stepNumber > currentStep) {
        // Validate current step before moving forward
        const errors = validateStep(currentStep, formData)
        if (errors.length === 0) {
          setCurrentStep(stepNumber)
        } else {
          setStepErrors((prev) => ({ ...prev, [currentStep]: errors }))
        }
      }
    }
  }, [currentStep])

  const handlePrevious = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }, [currentStep])

  const handleNext = useCallback((formData: ProductData) => {
    const errors = validateStep(currentStep, formData)
    if (errors.length === 0) {
      setStepErrors((prev) => ({ ...prev, [currentStep]: [] }))
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1)
        window.scrollTo({ top: 0, behavior: "smooth" })
      }
    } else {
      setStepErrors((prev) => ({ ...prev, [currentStep]: errors }))
    }
  }, [currentStep, totalSteps])

  const clearStepErrors = useCallback((step: number) => {
    setStepErrors((prev) => ({ ...prev, [step]: [] }))
  }, [])

  const resetNavigation = useCallback(() => {
    setCurrentStep(1)
    setStepErrors({})
  }, [])

  return {
    currentStep,
    setCurrentStep,
    stepErrors,
    setStepErrors,
    handleStepClick,
    handlePrevious,
    handleNext,
    clearStepErrors,
    resetNavigation
  }
}

// Hook for managing unsaved changes
export function useWizardUnsavedChanges(componentId: string = "ProductCreationWizard") {
  const { setHasUnsavedChanges, unregisterComponent } = useComponentUnsavedChanges(componentId)
  const [localHasUnsavedChanges, setLocalHasUnsavedChanges] = useState(false)
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)
  const [pendingLocation, setPendingLocation] = useState<Location | null>(null)
  const isInitialLoadRef = useRef(true)

  const updateUnsavedChangesState = useCallback((hasChanges: boolean) => {
    setLocalHasUnsavedChanges(hasChanges)
    setHasUnsavedChanges(hasChanges)
  }, [setHasUnsavedChanges])

  const checkForUnsavedChanges = useCallback((currentData: ProductData) => {
    // Don't check for changes during initial load
    if (isInitialLoadRef.current) {
      return false
    }

    const hasUnsavedData = checkForMeaningfulData(currentData)
    updateUnsavedChangesState(hasUnsavedData)
    return hasUnsavedData
  }, [updateUnsavedChangesState])

  const setInitialLoadComplete = useCallback(() => {
    isInitialLoadRef.current = false
  }, [])

  const resetInitialLoad = useCallback(() => {
    isInitialLoadRef.current = true
    setTimeout(() => {
      isInitialLoadRef.current = false
    }, 100)
  }, [])

  return {
    localHasUnsavedChanges,
    showUnsavedChangesModal,
    setShowUnsavedChangesModal,
    pendingLocation,
    setPendingLocation,
    updateUnsavedChangesState,
    checkForUnsavedChanges,
    unregisterComponent,
    isInitialLoadRef,
    setInitialLoadComplete,
    resetInitialLoad
  }
}

// Hook for managing wizard persistence
export function useWizardPersistence(
  persistData: boolean,
  persistKey: string,
  mode: 'create' | 'edit' = 'create'
) {
  // Handle initial data loading
  const loadInitialData = useCallback((initialData?: Partial<ProductData>) => {
    if (!persistData) return null

    // Check if this was a page reload
    const wasReloaded = checkAndClearReloadFlag(persistKey)
    
    if (wasReloaded) {
      // User confirmed reload - clear the data as expected
      console.log("Page was reloaded by user choice - clearing form data")
      clearPersistedData(persistKey)
      return null
    } else if (mode === 'create') {
      // Only restore localStorage data for create mode
      return loadFormData(persistKey, initialData, mode)
    }
    
    return null
  }, [persistData, persistKey, mode])

  // Save data effect
  const persistFormData = useCallback((formData: ProductData) => {
    if (persistData) {
      saveFormData(persistKey, formData)
    }
  }, [persistData, persistKey])

  // Handle before unload
  const handleBeforeUnload = useCallback((event: BeforeUnloadEvent, hasUnsavedChanges: boolean) => {
    if (hasUnsavedChanges) {
      if (persistData) {
        setReloadFlag(persistKey)
      }
      event.preventDefault()
      event.returnValue = ""
      return ""
    }
  }, [persistData, persistKey])

  // Setup event listeners
  useEffect(() => {
    const handlePageShowEvent = (event: PageTransitionEvent) => {
      handlePageShow(event, persistKey)
    }

    window.addEventListener("pageshow", handlePageShowEvent)
    return () => {
      window.removeEventListener("pageshow", handlePageShowEvent)
    }
  }, [persistKey])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupOnUnmount(persistData, persistKey)
    }
  }, [persistData, persistKey])

  return {
    loadInitialData,
    persistFormData,
    handleBeforeUnload,
    clearPersistedData: () => clearPersistedData(persistKey)
  }
}

// Hook for handling step data changes
export function useStepDataHandlers(
  formData: ProductData,
  updateFormData: (stepKey: keyof ProductData, data: any) => void,
  checkForUnsavedChanges: (data: ProductData) => boolean,
  clearStepErrors: (step: number) => void,
  isInitialLoadRef: React.MutableRefObject<boolean>
) {
  const handleBasicInfoChange = useCallback((data: BasicInfoData) => {
    const newFormData = { ...formData, basicInfo: data }
    updateFormData('basicInfo', data)
    if (!isInitialLoadRef.current) {
      checkForUnsavedChanges(newFormData)
    }
    clearStepErrors(1)
  }, [formData, updateFormData, checkForUnsavedChanges, clearStepErrors, isInitialLoadRef])

  const handleClassificationChange = useCallback((data: ClassificationData) => {
    const newFormData = { ...formData, classification: data }
    updateFormData('classification', data)
    if (!isInitialLoadRef.current) {
      checkForUnsavedChanges(newFormData)
    }
    clearStepErrors(2)
  }, [formData, updateFormData, checkForUnsavedChanges, clearStepErrors, isInitialLoadRef])

  const handleSalesInfoChange = useCallback((data: SalesInfoData) => {
    const newFormData = { ...formData, salesInfo: data }
    updateFormData('salesInfo', data)
    if (!isInitialLoadRef.current) {
      checkForUnsavedChanges(newFormData)
    }
    clearStepErrors(3)
  }, [formData, updateFormData, checkForUnsavedChanges, clearStepErrors, isInitialLoadRef])

  const handleShippingChange = useCallback((data: ShippingData) => {
    const newFormData = { ...formData, shipping: data }
    updateFormData('shipping', data)
    if (!isInitialLoadRef.current) {
      checkForUnsavedChanges(newFormData)
    }
    clearStepErrors(4)
  }, [formData, updateFormData, checkForUnsavedChanges, clearStepErrors, isInitialLoadRef])

  const handleReviewChange = useCallback((data: ReviewData) => {
    const newFormData = { ...formData, review: data }
    updateFormData('review', data)
    if (!isInitialLoadRef.current) {
      checkForUnsavedChanges(newFormData)
    }
    clearStepErrors(5)
  }, [formData, updateFormData, checkForUnsavedChanges, clearStepErrors, isInitialLoadRef])

  return {
    handleBasicInfoChange,
    handleClassificationChange,
    handleSalesInfoChange,
    handleShippingChange,
    handleReviewChange
  }
}